import { XMarkIcon } from '@heroicons/react/24/outline';

import { Job, JobStatus } from '@/types';
import { getJobStatus } from '@/utils/job';

interface JobDetailPanelProps {
  job: Job;
  onClose: () => void;
}

/**
 * Compact detail panel for a selected job row.
 * Shows Config key-value pairs, Processed count (for completed jobs), and Comment.
 */
export function JobDetailPanel({ job, onClose }: JobDetailPanelProps) {
  const config = job.config as Record<string, string> | null;
  const hasConfig =
    config && typeof config === 'object' && Object.keys(config).length > 0;
  const status = getJobStatus(job);
  const isCompleted = status === JobStatus.Pass || status === JobStatus.Fail;

  return (
    <div
      data-testid="job-detail-panel"
      className="border-chariot-stroke bg-chariot-background-primary mb-2 rounded-lg border px-4 py-3"
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-chariot-text-primary text-sm font-semibold">
          Job Details
        </h4>
        <button
          onClick={onClose}
          className="text-chariot-text-secondary hover:text-chariot-text-primary"
          aria-label="Close detail panel"
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Config section */}
        <div className="md:col-span-2">
          <h5 className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wider uppercase">
            Configuration
          </h5>
          {hasConfig ? (
            <div className="grid max-h-24 gap-1 overflow-y-auto pr-1">
              {Object.entries(config).map(([key, value]) => (
                <div key={key} className="flex gap-2 text-sm">
                  <span className="text-chariot-text-secondary shrink-0 font-medium">
                    {key}:
                  </span>
                  <span className="text-chariot-text-primary break-all">
                    {String(value)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-chariot-text-secondary text-sm">
              No configuration
            </p>
          )}
        </div>

        {/* Processed section - only for completed jobs */}
        {isCompleted && (
          <div>
            <h5 className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
              Items Processed
            </h5>
            <span className="text-chariot-text-primary text-sm">
              {job.processed ?? 0}
            </span>
          </div>
        )}

        {/* Comment section */}
        {job.comment && (
          <div>
            <h5 className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
              Comment
            </h5>
            <span className="text-chariot-text-primary text-sm">
              {job.comment}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
